import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../lib/supabase';

// Função para formatar número de telefone
const formatPhone = (value) => {
  if (!value) return '';
  
  // Remove tudo que não for número
  const numbers = value.replace(/\D/g, '');
  
  // Aplica a máscara conforme quantidade de dígitos
  if (numbers.length <= 2) {
    return numbers;
  }
  if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  }
  if (numbers.length <= 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  }
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

function Clients() {
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientsInUse, setClientsInUse] = useState(new Set());
  const [formData, setFormData] = useState({
    company: '',
    contact_name: '',
    website: '',
    phone: '',
    country: 'Brasil',
    is_active: true
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      // Buscar clientes
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .order('company');

      if (clientsError) throw clientsError;

      // Buscar serviços dos clientes
      const { data: servicesData, error: servicesError } = await supabase
        .from('client_services')
        .select('client_id');

      if (servicesError) throw servicesError;

      // Criar um Set com os IDs dos clientes em uso
      const inUse = new Set(servicesData.map(s => s.client_id));
      setClientsInUse(inUse);
      
      setClients(clientsData || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData(client);
    } else {
      setEditingClient(null);
      setFormData({
        company: '',
        contact_name: '',
        website: '',
        phone: '',
        country: 'Brasil',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Aplica formatação para campos específicos
    if (name === 'phone') {
      formattedValue = formatPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        const { error } = await supabase
          .from('clients')
          .update(formData)
          .eq('id', editingClient.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('clients')
          .insert([formData]);

        if (error) throw error;
      }

      handleCloseDialog();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleDelete = async (id) => {
    if (clientsInUse.has(id)) {
      alert('Não é possível excluir este cliente pois ele possui serviços associados');
      return;
    }

    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        const { error } = await supabase
          .from('clients')
          .delete()
          .eq('id', id);

        if (error) {
          if (error.code === '23503') { // Código de violação de chave estrangeira
            alert('Não é possível excluir este cliente pois ele possui serviços associados');
          } else {
            throw error;
          }
          return;
        }
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Erro ao excluir cliente: ' + error.message);
      }
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'flex-start' : 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        gap: isMobile ? 2 : 0,
        mb: 3 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
          Clientes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ minWidth: isMobile ? '100%' : 'auto' }}
        >
          Novo Cliente
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {clients.map((client) => (
            <Paper key={client.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {client.company}
                  </Typography>
                  {client.contact_name && (
                    <Typography variant="body2" color="text.secondary">
                      {client.contact_name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(client)}
                    sx={{ 
                      minWidth: '44px',
                      minHeight: '44px'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title={clientsInUse.has(client.id) ? "Não é possível excluir um cliente que possui serviços associados" : ""}>
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(client.id)}
                        disabled={clientsInUse.has(client.id)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {client.website && (
                  <Link 
                    href={client.website.startsWith('http') ? client.website : `https://${client.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#1976d2', 
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    {client.website}
                  </Link>
                )}
                {client.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {client.phone}
                  </Typography>
                )}
                {client.country && (
                  <Typography variant="body2" color="text.secondary">
                    {client.country}
                  </Typography>
                )}
              </Box>

              <Chip 
                label={client.is_active ? "Ativo" : "Inativo"}
                color={client.is_active ? "success" : "default"}
                size="small"
              />
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Website</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>País</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.company}</TableCell>
                  <TableCell>{client.contact_name}</TableCell>
                  <TableCell>
                    {client.website && (
                      <Link 
                        href={client.website.startsWith('http') ? client.website : `https://${client.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        {client.website}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>{client.phone}</TableCell>
                  <TableCell>{client.country}</TableCell>
                  <TableCell>
                    <Chip 
                      label={client.is_active ? "Ativo" : "Inativo"}
                      color={client.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(client)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Tooltip title={clientsInUse.has(client.id) ? "Não é possível excluir um cliente que possui serviços associados" : ""}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(client.id)}
                            disabled={clientsInUse.has(client.id)}
                            sx={{ 
                              minWidth: '44px',
                              minHeight: '44px'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingClient ? 'Editar Cliente' : 'Novo Cliente'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="company"
                label="Empresa"
                value={formData.company}
                onChange={handleChange}
                fullWidth
                required
              />
              
              <TextField
                name="contact_name"
                label="Nome do Contato"
                value={formData.contact_name}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                name="website"
                label="Website"
                value={formData.website}
                onChange={handleChange}
                fullWidth
                placeholder="www.empresa.com.br"
              />

              <TextField
                name="phone"
                label="Telefone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                name="country"
                label="País"
                value={formData.country}
                onChange={handleChange}
                fullWidth
                placeholder="Brasil"
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value={true}>Ativo</MenuItem>
                  <MenuItem value={false}>Inativo</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Clients;
